import React, { useRef, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { runInAction } from 'mobx';
import { Spinner } from 'reactstrap';
import { useRootStore } from '../../Store/useRootStore';
import { useToast } from '../../Hooks/useToast';
import { useSaveAthlete } from '../../Hooks/useAthleteMutations';
import { useAuth0 } from '@auth0/auth0-react';
import { Theme } from '../../Theme/Theme';
import { MediaService } from '../../Services/MediaService';
import { useTrans } from '../../Store/System/LocalizationStore';
import { getMediaMedium, getMediaSmallest } from '../../Model/Media/Media';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const Container = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AvatarContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Avatar = styled.div<{ $uploading?: boolean }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${Theme.Colors.lightestGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  animation: ${(props) => (props.$uploading ? pulse : 'none')} 1.5s ease-in-out infinite;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  i {
    font-size: 48px;
    color: ${Theme.Colors.warmGrey};
  }
`;

const EditButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${Theme.Colors.primary};
  border: 2px solid white;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: ${Theme.Colors.primaryDark || '#0056b3'};
    transform: scale(1.1);
  }

  &:disabled {
    background: ${Theme.Colors.grey};
    cursor: not-allowed;
  }

  i {
    font-size: 14px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const Name = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 4px 0;
`;

const Nickname = styled.p`
  font-size: 15px;
  font-weight: 500;
  color: ${Theme.Colors.grey};
  margin: 0 0 4px 0;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Email = styled.p`
  font-size: 13px;
  margin: 0;
  color: ${Theme.Colors.charcoal};
`;

const VerificationBadge = styled.span<{ $verified: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  background: ${(props) => (props.$verified ? `${Theme.Colors.success}15` : '#fef2f2')};
  color: ${(props) => (props.$verified ? Theme.Colors.success : '#dc2626')};
  white-space: nowrap;

  i {
    font-size: 12px;
  }
`;

const UploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ProfileHeader: React.FC = observer(() => {
  const { authentication } = useRootStore();
  const toast = useToast();
  const { user: auth0User } = useAuth0();
  const { t } = useTrans();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const athlete = authentication.athlete;
  const profilePicture = athlete?.profilePicture ? getMediaMedium(athlete.profilePicture) : undefined;
  const saveAthlete = useSaveAthlete();

  const handleEditClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !athlete) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.showToast(t('profile.picture.invalidType', { defaultValue: 'Please select an image file' }), 'error');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.showToast(t('profile.picture.tooLarge', { defaultValue: 'Image must be smaller than 10MB' }), 'error');
        return;
      }

      setUploading(true);

      try {
        // Upload via /media/media endpoint with automatic retries
        const media = await MediaService.uploadMedia(file);

        // Set the media as profilePicture on athlete and save
        runInAction(() => {
          if (athlete) {
            athlete.profilePicture = media;
          }
        });

        // Save the athlete with the new profile picture
        await saveAthlete.mutateAsync(athlete);

        toast.showToast(t('profile.picture.success', { defaultValue: 'Profile picture updated!' }), 'success');
      } catch (error) {
        console.error('Error uploading profile picture:', error);
        toast.showToast(
          t('profile.picture.error', { defaultValue: 'Failed to upload profile picture. Please try again.' }),
          'error',
        );
      } finally {
        setUploading(false);
        // Reset input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [athlete, toast, t, saveAthlete],
  );

  return (
    <Container>
      <AvatarContainer>
        <Avatar $uploading={uploading}>
          {profilePicture ? <img src={profilePicture} alt="Profile" /> : <i className="bi bi-person-fill" />}
        </Avatar>
        {uploading && (
          <UploadingOverlay>
            <Spinner size="sm" color="light" />
          </UploadingOverlay>
        )}
        <EditButton
          onClick={handleEditClick}
          disabled={uploading}
          title={t('profile.picture.change', { defaultValue: 'Change photo' })}
        >
          <i className="bi bi-camera-fill" />
        </EditButton>
        <HiddenInput ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} />
      </AvatarContainer>

      <ProfileInfo>
        <Name>{athlete?.firstname || 'User'}</Name>
        {athlete?.nickname && <Nickname>@{athlete.nickname}</Nickname>}
        <EmailRow>
          <Email>{auth0User?.email}</Email>
          {auth0User?.email && (
            <VerificationBadge $verified={!!auth0User.email_verified}>
              <i className={auth0User.email_verified ? 'bi bi-check-circle-fill' : 'bi bi-exclamation-circle-fill'} />
              {auth0User.email_verified
                ? t('profile.email.verified', { defaultValue: 'Verified' })
                : t('profile.email.unverified', { defaultValue: 'Not verified' })}
            </VerificationBadge>
          )}
        </EmailRow>
      </ProfileInfo>
    </Container>
  );
});
